import { useMemo, useState } from 'react';
import DataTable, { Column } from '@/components/common/DataTable';
import type { Redemption } from '@/types/reward';

const statusStyles: Record<string, { label: string; classes: string; dot: string }> = {
  pending: { label: 'Pending', classes: 'bg-secondary-100 text-secondary-800', dot: 'bg-secondary-500' },
  approved: { label: 'Approved', classes: 'bg-primary-100 text-primary-700', dot: 'bg-primary-500' },
  rejected: { label: 'Rejected', classes: 'bg-background-200 text-foreground-600', dot: 'bg-foreground-400' },
  fulfilled: { label: 'Fulfilled', classes: 'bg-accent-100 text-accent-800', dot: 'bg-accent-500' },
};

interface RedemptionTableProps {
  data: Redemption[];
  onApprove: (redemption: Redemption) => void;
  onReject: (redemption: Redemption) => void;
  onFulfill: (redemption: Redemption) => void;
}

export default function RedemptionTable({ data, onApprove, onReject, onFulfill }: RedemptionTableProps) {
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);

  const columns: Column<Redemption>[] = useMemo(
    () => [
      {
        key: 'id',
        header: 'Request',
        title: 'Request ID',
        sortable: true,
        render: (item) => (
          <span className="font-mono text-xs text-foreground-700 bg-background-100/90 px-2 py-0.5 rounded-md border border-background-200/70 whitespace-nowrap">
            {item.id}
          </span>
        ),
      },
      {
        key: 'member',
        header: 'Member',
        title: 'Member Info',
        sortable: true,
        render: (item) => (
          <div className="min-w-0">
            <p className="font-semibold text-foreground-950 truncate">{item.member}</p>
            <p className="text-xs text-foreground-500 truncate">{item.platform}</p>
          </div>
        ),
      },
      {
        key: 'reward',
        header: 'Reward',
        title: 'Reward Item',
        sortable: true,
        render: (item) => <span className="text-foreground-800 font-medium">{item.reward}</span>,
      },
      {
        key: 'pointsSpent',
        header: 'Points',
        title: 'Points Spent',
        sortable: true,
        align: 'right',
        sortValue: (r) => r.pointsSpent,
        render: (item) => (
          <span className="font-bold text-secondary-700 tabular-nums whitespace-nowrap">
            -{item.pointsSpent.toLocaleString()}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        title: 'Status',
        sortable: true,
        render: (item) => {
          const style = statusStyles[item.status] ?? statusStyles.pending;
          return (
            <span
              className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap shadow-2xs ${style.classes}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`}></span>
              {style.label}
            </span>
          );
        },
      },
      {
        key: 'requestedAt',
        header: 'Requested',
        title: 'Requested Date',
        sortable: true,
        align: 'right',
        render: (item) => (
          <span className="text-foreground-500 text-xs whitespace-nowrap">{item.requestedAt}</span>
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        title: 'Actions',
        align: 'right',
        hideable: false,
        render: (item) => (
          <div className="flex items-center justify-end gap-1.5">
            {item.status === 'pending' && (
              <>
                <button
                  type="button"
                  onClick={() => onApprove(item)}
                  className="whitespace-nowrap inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary-100 text-primary-700 text-xs font-bold hover:bg-primary-200 transition-colors cursor-pointer shadow-2xs"
                >
                  <i className="ri-check-line"></i> Approve
                </button>
                <button
                  type="button"
                  onClick={() => onReject(item)}
                  className="whitespace-nowrap inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-background-100 border border-background-200 text-foreground-600 text-xs font-bold hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line"></i> Reject
                </button>
              </>
            )}
            {item.status === 'approved' && (
              <button
                type="button"
                onClick={() => onFulfill(item)}
                className="whitespace-nowrap inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-accent-100 text-accent-800 text-xs font-bold hover:bg-accent-200 transition-colors cursor-pointer shadow-2xs"
              >
                <i className="ri-check-double-line"></i> Fulfill
              </button>
            )}
            {(item.status === 'fulfilled' || item.status === 'rejected') && (
              <span className="text-xs text-foreground-400 whitespace-nowrap">
                {item.note ? 'Noted' : '\u2014'}
              </span>
            )}
          </div>
        ),
      },
    ],
    [onApprove, onReject, onFulfill]
  );

  return (
    <DataTable
      data={data}
      columns={columns}
      getRowId={(r) => r.id}
      selectable={true}
      selectedIds={selectedIds}
      onSelectionChange={(ids) => setSelectedIds(ids)}
      columnVisibility={true}
      exportable={true}
      exportFilename="redemptions-export.csv"
      pageSize={8}
      pageSizeOptions={[8, 16, 32]}
      minWidth="920px"
      emptyIcon="ri-gift-line"
      emptyTitle="No redemption requests found"
      emptyDescription="Try adjusting your status filter or clearing your search."
    />
  );
}
